import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Skeleton component with exact Hyperkit styling
 *
 * Hyperkit Specs:
 * - Shimmer animation effect (gradient sweep)
 * - Border radius matches element being loaded
 * - Proper contrast in both themes
 *
 * Light mode: bg-grey-200 shimmer to bg-grey-100
 * Dark mode: bg-grey-800 shimmer to bg-grey-700
 *
 * @example
 * // Text skeleton
 * <Skeleton variant="text" className="h-4 w-48" />
 *
 * // Circular skeleton (avatar)
 * <Skeleton variant="circular" width={40} height={40} />
 *
 * // Rectangular skeleton (card/image)
 * <Skeleton variant="rectangular" className="h-32 w-full" />
 */
const skeletonVariants = cva(
  // Base: shimmer animation with Hyperkit colors
  [
    'bg-[length:200px_100%]',
    'bg-gradient-to-r',
    'from-grey-200 via-grey-100 to-grey-200',
    'dark:from-grey-800 dark:via-grey-700 dark:to-grey-800',
    'animate-skeleton-shimmer',
  ],
  {
    variants: {
      variant: {
        // Text: 4px radius - for text placeholders
        text: 'rounded',
        // Circular: full radius - for avatars
        circular: 'rounded-full',
        // Rectangular: 6px radius - for cards, images, containers
        rectangular: 'rounded-md',
      },
    },
    defaultVariants: {
      variant: 'rectangular',
    },
  }
);

const Skeleton = React.forwardRef(
  ({ className, variant, width, height, style, ...props }, ref) => {
    // Combine explicit width/height props with any inline styles
    const combinedStyle = {
      ...style,
      ...(width !== undefined && { width: typeof width === 'number' ? `${width}px` : width }),
      ...(height !== undefined && { height: typeof height === 'number' ? `${height}px` : height }),
    };

    return (
      <div
        ref={ref}
        className={cn(skeletonVariants({ variant }), className)}
        style={Object.keys(combinedStyle).length > 0 ? combinedStyle : undefined}
        aria-hidden="true"
        {...props}
      />
    );
  }
);
Skeleton.displayName = 'Skeleton';

export { Skeleton, skeletonVariants };
